import {computed, inject, Injectable, Signal} from '@angular/core';
import {StorageKeys} from '@core/storage';
import {SignalStore} from '@feature-signal-store';
import {catchError, filter, finalize, first, map, of} from 'rxjs';
import {useDevtools, useStorePersistence} from 'signalstory';
import {VersionService} from '../services';
import {appInfoInitialState, IAppInfoState} from './app-info.state';

type StringBuilderFn = (frontEnd: string, backEnd: string) => string;

@Injectable({
	providedIn: 'root',
})
export class AppInfoStore extends SignalStore<IAppInfoState> {
	/* ------------ Providers / Services ------------ */
	private readonly versionService: VersionService = inject(VersionService);

	/* ---------------- Properties ---------------- */
	/* ---------------- Computed ---------------- */
	public readonly appVersionsString$: Signal<string> = computed(() => {
		const frontEnd = this.state().frontEnd;
		const backEnd = this.state().backEnd;
		return `FrontEnd: ${frontEnd}\nBackEnd: ${backEnd}`;
	});

	public appVersionStringFactory(stringBuilderFn: StringBuilderFn): Signal<string> {
		return computed(() => stringBuilderFn(this.state().frontEnd, this.state().backEnd));
	}

	/* ---------------- Constructor ---------------- */
	constructor() {
		super('App Info Store', 'Global', appInfoInitialState, {
			plugins: [
				useDevtools(),
				useStorePersistence({
					persistenceKey: StorageKeys.Stores.APP_INFO_STORE,
					persistenceStorage: localStorage,
				}),
			],
		});
		this.Version.load(true);
	}

	/* -------------------------------- Http Methods -------------------------------- */

	public readonly Version = {
		load: (force = false) => {
			if (!force && this.state().frontEnd && this.state().backEnd) return;
			if (!force && this.state()?.loading) return;
			this.update(state => ({...state, loading: true}), 'Load Version');
			this.versionService
				.getVersion()
				.pipe(
					filter(Boolean),
					first(),
					map(version => {
						this.update(state => {
							return {
								...state,
								frontEnd: version.frontEnd,
								backEnd: version.backEnd,
							};
						});
						return version;
					}),
					catchError(error => of(error)),
					finalize(() => {
						this.update(state => ({...state, loading: false}), 'Load Version - Finalize');
					}),
				)
				.subscribe();
		},
	};

	/* -------------------------------- State Methods -------------------------------- */
	public setAppInfoIndicator(isPwaInstalled: boolean): void {
		this.mutate(state => ({...state, indicators: {...state.indicators, isPwaInstalled}}), 'Set App Info Indicator');
	}

	/* -------------------------------- Private Methods -------------------------------- */
}
